import { apiClient } from '../auth-lib/utils/axiosClient';

// The command layer's client half.
//
// ⚠️ `apiClient`, NOT `axiosClient`. This talks to the hrms backend; axiosClient
// points at the auth service. Mixing them up is the standing trap on any screen
// that touches both, and here every call is hrms.

/**
 * Search everything the caller is allowed to see.
 *
 * ⚠️ READ-ONLY, AND A DIFFERENT ENDPOINT FROM parseCommand. A search box that
 * could occasionally create a task because the words looked like an order is
 * the one behaviour a search box must never have.
 */
export async function searchEverything({ q, types, signal }) {
  const { data } = await apiClient.get('/api/ai/search', {
    params: { q, types: types?.length ? types.join(',') : undefined },
    signal,
  });
  return data;
}

/**
 * Ask a question. Read-only — it can never touch the practice's records.
 *
 * ⚠️ NO `history` PARAMETER, DELIBERATELY. It used to send the last ten turns
 * from React state, which is why the conversation died on a refresh — the
 * browser was the only thing that held it. The thread now lives on the server
 * and the model is fed from that copy, so all this has to name is which
 * conversation the question belongs to.
 *
 * Omit `conversationId` to start a new thread; the response carries the id of
 * whichever thread it landed in.
 */
export async function askAi({ text, conversationId, signal }) {
  const { data } = await apiClient.post('/api/ai/chat', { text, conversationId }, { signal });
  return data;
}

/** This user's stored Ask threads, newest first. Titles and previews only. */
export async function listConversations(limit) {
  const { data } = await apiClient.get('/api/ai/conversations', { params: { limit } });
  return data.conversations || [];
}

/**
 * One thread's transcript, oldest-first.
 *
 * `before` pages backwards through a long conversation — pass the `at` of the
 * oldest message already on screen.
 */
export async function getConversation(id, { before, limit, signal } = {}) {
  const { data } = await apiClient.get(`/api/ai/conversations/${id}`, {
    params: { before, limit }, signal,
  });
  return data;
}

export async function renameConversation(id, title) {
  const { data } = await apiClient.patch(`/api/ai/conversations/${id}`, { title });
  return data;
}

export async function deleteConversation(id) {
  const { data } = await apiClient.delete(`/api/ai/conversations/${id}`);
  return data;
}

/**
 * Commands this user has typed, and what each one did.
 *
 * ⚠️ THE ROWS ALWAYS EXISTED — `/command` and `/execute` have been writing
 * `CommandLog` since the feature shipped. The AI page's "Just now" list was a
 * React array, so the record was in the database and the screen threw it away
 * on every refresh. This is the read that was missing.
 */
export async function commandHistory({ before, limit, executedOnly } = {}) {
  const { data } = await apiClient.get('/api/ai/history', {
    params: { before, limit, executedOnly: executedOnly ? 'true' : undefined },
  });
  return data;
}

/** Is the LLM tier switched on for this firm, and may this user change it? */
export async function getAiSettings() {
  const { data } = await apiClient.get('/api/ai/settings');
  return data;
}

export async function updateAiSettings(patch) {
  const { data } = await apiClient.put('/api/ai/settings', patch);
  return data;
}

/** Which result types this caller may search — drives the type filter chips. */
export async function searchSources() {
  const { data } = await apiClient.get('/api/ai/sources');
  return data.sources || [];
}

/** Live autocomplete for one slot. Powers the chip bar. */
export async function resolveEntity({ type, q, limit = 5, signal }) {
  const { data } = await apiClient.get('/api/ai/resolve', {
    params: { type, q, limit },
    signal,
  });
  return data;
}

/** Parse a typed line. Writes nothing — safe to call on every submit. */
export async function parseCommand(text) {
  const { data } = await apiClient.post('/api/ai/command', { text });
  return data;
}

/**
 * Execute a parsed command.
 *
 * ⚠️ THE IDEMPOTENCY KEY IS GENERATED ONCE PER CONFIRMATION AND REUSED ON
 * RETRY. Generating a fresh one per attempt would defeat the entire mechanism —
 * a user who double-taps, or a network retry, would each get a new key and the
 * server would happily create two sets of forty tasks.
 */
export async function executeCommand({ commandLogId, idempotencyKey, corrections, clientIds }) {
  const { data } = await apiClient.post('/api/ai/execute', {
    commandLogId, idempotencyKey, corrections, clientIds,
  });
  return data;
}

// crypto.randomUUID is available in every browser this app supports over https;
// the fallback covers a dev machine on plain http, where it is undefined.
//
// ⚠️ `window`, NOT `globalThis`. CRA's eslint config (`extends: react-app`) does
// not declare `globalThis` as a known global, so it fails `no-undef` — and
// react-scripts treats a lint error as a BUILD failure, not a warning. It runs
// clean in dev and breaks `npm run build`, which is the worst place to find it.
// This is browser-only code, so `window` is both correct and already in scope.
export const newIdempotencyKey = () => (
  window.crypto?.randomUUID?.()
  || `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

// Re-exported so a caller that already imports this module gets it, without a
// seventh copy of the same four lines existing anywhere.
export { formatTaskNumber } from './taskRef';
